import React, { useEffect, useRef, useState } from 'react';
import { ExposureTypes, getExposreDesc, getExposureColor } from '../../utils/RiskUtils';
import './MaterialTrendButton.scss';

export interface singleMaterialCardProps {
  materialName: string;
  materialId: string;
  exposureStatus: ExposureTypes;
  isSelected?: boolean;
  cardColor?: string;
}

const SingleMaterialCard: React.FC<singleMaterialCardProps> = ({
  materialName,
  cardColor,
  exposureStatus,
  isSelected,
}) => {
  return (
    <div
      className={`flex flex-col shadow-custom min-w-40 p-2 my-1 rounded border-l-8 bg-white hover:shadow-custom hover:bg-gray-75 ${
        isSelected ? 'outline outline-2 outline-red' : ''
      } transform hover:scale-105  transition-transform duration-300 `}
      style={{ borderColor: cardColor, boxShadow: '0 0 0.5rem #ccc' }}
    >
      <div className="flex flex-row  justify-between align-middle">
        <p className="text-sm font-medium m-0">{materialName}</p>
        <div style={{ backgroundColor: getExposureColor(exposureStatus) }} className={`w-4 h-4 rounded-full  `}></div>
      </div>
    </div>
  );
};

interface MaterialTrendButtonProps {
  buttons: singleMaterialCardProps[];
  currentMaterial: string;
  setCurrentMaterial: (_materialName: string) => void;
}

export const MaterialTrendButton: React.FC<MaterialTrendButtonProps> = ({
  buttons,
  currentMaterial,
  setCurrentMaterial,
}) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateScrollButtons = () => {
      const current = scrollContainerRef.current;
      if (!current) return;

      setCanScrollLeft(current.scrollLeft > 0);
      setCanScrollRight(current.scrollLeft + current.clientWidth < current.scrollWidth);
    };

    updateScrollButtons();
    const current = scrollContainerRef.current;
    current?.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);

    return () => {
      current?.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [buttons]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  return (
    <div className="flex w-full ">
      {canScrollLeft && (
        <div className="flex flex-col min-w-[2.35rem] shadow-md me-[0.3rem]">
          <button onClick={scrollLeft} className="bg-white rounded h-full disabled:opacity-50">
            <img className="h-full w-full" title="left" src="/assets/icons/flat/chevron_left.svg" />
          </button>
        </div>
      )}
      <div ref={scrollContainerRef} className="flex gap-[0.75rem] px-2  whitespace-nowrap overflow-hidden">
        {buttons.map((material, index) => (
          <button
            key={index}
            title={`switch to ${material.materialName} view (${getExposreDesc(material.exposureStatus)})`}
            className="rounded  "
            onClick={() => setCurrentMaterial(material.materialId)}
          >
            <SingleMaterialCard
              materialId={material.materialId}
              materialName={material.materialName}
              cardColor={material.cardColor}
              isSelected={
                material.materialId === currentMaterial || (!currentMaterial && material.materialId === 'general')
              }
              exposureStatus={material.exposureStatus}
            />
          </button>
        ))}
      </div>

      {canScrollRight && (
        <div className="flex flex-col min-w-[2.35rem] shadow-md ms-[0.3rem]">
          <button onClick={scrollRight} className="bg-white rounded h-full disabled:opacity-50">
            <img className="h-full w-full" title="right" src="/assets/icons/flat/chevron_right.svg" />
          </button>
        </div>
      )}
    </div>
  );
};
